import { getAttributeOrThrow, qsaOptional, qsRequired } from '@/scripts/core/global'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { mediaLoader } from '@/scripts/core/global'

export class PaginateButton extends UcoastEl {
	static htmlSelector = 'paginate-button'
	button: HTMLButtonElement
	currentSort: ArticleSort = 'created-descending'
	articles?: ArticleReference[]
	constructor() {
		super()
		this.button = qsRequired('button', this)
	}

	getInit(): ComponentInit {
		const currentPage = parseInt(getAttributeOrThrow('data-current-page', this))
		const container = qsRequired(`[data-pagination-container]`)
		const fetchUrl = getAttributeOrThrow('data-url', this)
		const sectionHandle = getAttributeOrThrow('data-section-handle', this)
		const pageSize = 12
		const sort = getAttributeOrThrow('data-sort', this)
		const totalPages = parseInt(getAttributeOrThrow('data-total-pages', this))
		if (!isSort(sort)) {
			throw new Error('Invalid sort value')
		}
		return {
			currentPage,
			container,
			fetchUrl,
			sectionHandle,
			pageSize,
			sort,
			totalPages,
		}
	}

	override connectedCallback() {
		super.connectedCallback()
		void this.getArticles()
		this.button.addEventListener('click', () => {
			void this.renderPage()
		})
	}
	override disconnectedCallback() {
		super.disconnectedCallback()
	}

	async loadPage({ fetchUrl, currentPage, sectionHandle }: ComponentInit) {
		const url = `${fetchUrl}?page=${currentPage}&section_id=${sectionHandle}`
		return await fetch(url)
			.then((response) => response.text())
			.then((text) => {
				return text
			})
			.catch((e) => {
				console.error(e)
				return 'ERROR'
			})
	}

	sort(sort: string) {
		if (!isSort(sort)) {
			throw new Error('Invalid sort value')
		}
		this.setAttribute('data-sort', sort)
		void this.renderPage()
	}

	async renderPage() {
		const { sort, currentPage, pageSize, container } = this.getInit()
		const articles = await this.getArticles()
		switch (sort) {
			case 'title-ascending':
				articles.sort((a, b) => a.title.localeCompare(b.title))
				break
			case 'title-descending':
				articles.sort((a, b) => b.title.localeCompare(a.title))
				break
			case 'created-ascending':
				articles.sort((a, b) => a.date - b.date)
				break
			case 'created-descending':
				articles.sort((a, b) => b.date - a.date)
				break
		}

		let pageToGet = this.currentSort !== sort ? 0 : currentPage + 1
		if (this.currentSort !== sort) {
			this.setAttribute('data-current-page', pageToGet.toString())
			pageToGet = 0
			this.currentSort = sort
		} else {
			this.setAttribute('data-current-page', pageToGet.toString())
		}
		if (pageToGet >= this.getTotalPages() - 1) {
			this.setAttribute('style', 'display: none;')
		}
		const html = this.getNextPage(articles, pageSize, pageToGet)
			.map((article) => article.html)
			.join('')

		if (pageToGet === 0) {
			container.innerHTML = html
		} else {
			container.innerHTML += html
		}

		mediaLoader()
	}

	getTotalPages() {
		// totalPages from init is 48 to reduce calls to api
		// however, the actual total will be around 4x that since we're rendering pages of 12
		if (!this.articles) {
			return 0
		}
		return Math.ceil(this.articles.length / this.getInit().pageSize)
	}

	async getArticles() {
		if (this.articles) {
			return this.articles
		}

		const init = this.getInit()
		const { totalPages } = init
		const promises: Promise<string>[] = []
		for (let i = 0; i < totalPages; i++) {
			promises.push(
				this.loadPage({
					...init,
					currentPage: init.currentPage + i,
				})
			)
		}
		const pages = await Promise.all(promises)

		const articles: ArticleReference[] = []
		const usedIds = new Set<number>()

		for (let i = 0; i < totalPages; i++) {
			const page = pages[i]
			const html = new DOMParser().parseFromString(page, 'text/html')

			const articleEls = qsaOptional('[data-article-reference]', html)

			if (!articleEls) {
				console.log('no els found', i)
				continue
			}
			articleEls.forEach((el) => {
				const id = parseInt(el.getAttribute('data-article-reference') || '0')
				if (usedIds.has(id)) {
					return
				}
				usedIds.add(id)
				const title = el.getAttribute('data-title') || ''
				const date = parseInt(el.getAttribute('data-date') || '0')
				const html = el.outerHTML
				articles.push({ title, date, html, id })
			})
		}
		this.articles = articles
		if (this.articles.length < 13) {
			this.setAttribute('style', 'display: none;');
		}
		return articles
	}
	getNextPage<T>(array: T[], pageSize: number, currentPage: number): T[] {
		const startIndex = currentPage * pageSize
		const endIndex = startIndex + pageSize
		return array.slice(startIndex, endIndex)
	}
}

function isSort(value: string): value is ArticleSort {
	return [
		'title-ascending',
		'title-descending',
		'created-ascending',
		'created-descending',
	].includes(value)
}

type ArticleSort =
	| 'title-ascending'
	| 'title-descending'
	| 'created-ascending'
	| 'created-descending'

type ComponentInit = {
	currentPage: number
	container: HTMLElement
	fetchUrl: string
	sectionHandle: string
	sort: ArticleSort
	pageSize: number
	totalPages: number
}

type ArticleReference = {
	html: string
	title: string
	date: number
	id: number
}
